import React, { useState, useRef, useEffect } from 'react';
import heic2any from 'heic2any';
import { uploadReceipt } from '../services/api';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

function ReceiptUpload({ onExpenseAdded, onBack }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setConsentGiven(localStorage.getItem('zenith_ocr_consent_given') === 'true');
  }, []);

  const handleConsentChange = (e) => {
    const isChecked = e.target.checked;
    setConsentGiven(isChecked);
    if (isChecked) {
      localStorage.setItem('zenith_ocr_consent_given', 'true');
    } else {
      localStorage.removeItem('zenith_ocr_consent_given');
    }
    setError(null);
  };

  const processFile = async (file) => {
    if (!file) return;

    // Validate file type (images and PDFs)
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Please select an image file (JPG, PNG, GIF, WebP, HEIC) or PDF');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError(null);
    setResult(null);

    // Create preview
    if (file.type === 'application/pdf') {
      // For PDFs, show a document icon instead of preview
      setSelectedFile(file);
      setPreview('pdf');
    } else if (file.type && (file.type.includes('heic') || file.type.includes('heif'))) {
      // For HEIC/HEIF files, convert to JPEG for preview
      try {
        setLoading(true);
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
        });

        // Create preview from converted blob
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelectedFile(file);
          setPreview(reader.result);
          setLoading(false);
        };
        reader.readAsDataURL(convertedBlob);
      } catch (err) {
        setError(`Failed to preview HEIC file: ${err.message}`);
        setLoading(false);
      }
    } else {
      // For standard images, show image preview
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const handleCapture = async (source) => {
    if (!consentGiven) {
      setError('Please consent to AI processing below before capturing a receipt.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: source
      });

      if (image && image.base64String) {
        const byteCharacters = atob(image.base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: `image/${image.format}` });
        const file = new File([blob], `receipt.${image.format}`, { type: `image/${image.format}` });
        await processFile(file);
      }
    } catch (err) {
      // user closed camera/picker
      console.log('User cancelled capture', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);

    try {
      const response = await uploadReceipt(selectedFile);
      setResult(response.expense);
      // Don't navigate yet - wait for user to choose "Upload Next" or "Finish Uploading"
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadNext = () => {
    // Reset form for next upload without navigating away
    handleReset();
  };

  const handleFinishUploading = () => {
    // Notify parent component with the expense data and navigate to expenses
    if (onExpenseAdded && result) {
      onExpenseAdded(result);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const styles = {
    container: {
      padding: 'var(--space-6)',
      maxWidth: '600px',
      margin: '0 auto',
    },
    uploadArea: {
      border: '2px dashed var(--color-border-default)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-10) var(--space-6)',
      textAlign: 'center',
      backgroundColor: 'var(--color-bg-muted)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
    },
    uploadAreaHover: {
      borderColor: 'var(--color-primary)',
      backgroundColor: 'var(--color-primary-subtle)',
    },
    uploadIcon: {
      fontSize: '48px',
      marginBottom: 'var(--space-4)',
      display: 'block',
    },
    preview: {
      marginTop: 'var(--space-6)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      boxShadow: 'var(--shadow-md)',
    },
    previewImage: {
      width: '100%',
      maxHeight: '400px',
      objectFit: 'contain',
      backgroundColor: 'var(--color-bg-muted)',
    },
    button: {
      padding: 'var(--space-3) var(--space-6)',
      borderRadius: 'var(--radius-lg)',
      border: 'none',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'var(--transition-normal)',
      marginTop: 'var(--space-4)',
    },
    primaryButton: {
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-text-inverse)',
    },
    secondaryButton: {
      backgroundColor: 'var(--color-bg-muted)',
      color: 'var(--color-text-secondary)',
      marginLeft: 'var(--space-3)',
      border: '1px solid var(--color-border-default)',
    },
    disabledButton: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    error: {
      padding: 'var(--space-3)',
      borderRadius: 'var(--radius-default)',
      backgroundColor: 'var(--color-error-light)',
      color: 'var(--color-error-dark)',
      marginTop: 'var(--space-4)',
      fontSize: 'var(--font-size-sm)',
    },
    result: {
      marginTop: 'var(--space-8)',
      padding: 'var(--space-6)',
      borderRadius: 'var(--radius-lg)',
      backgroundColor: 'var(--color-success-light)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
    },
    resultItem: {
      marginBottom: 'var(--space-3)',
    },
    label: {
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-primary)',
      marginRight: 'var(--space-2)',
    },
    value: {
      color: 'var(--color-text-secondary)',
      fontWeight: 'var(--font-weight-normal)',
    },
    loader: {
      display: 'inline-block',
      width: '16px',
      height: '16px',
      border: '2px solid var(--color-border-light)',
      borderTop: '2px solid var(--color-primary)',
      borderRadius: 'var(--radius-full)',
      animation: 'spin 1s linear infinite',
      marginRight: 'var(--space-2)',
    },
  };

  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <h2 style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          letterSpacing: '-0.025em',
          margin: 0,
        }}>
          Upload Receipt
        </h2>
        {onBack && (
          <button 
            onClick={onBack}
            style={{
              padding: '8px 12px',
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border-default)',
              borderRadius: 'var(--radius-md)',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
        )}
      </div>

      {!preview ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {Capacitor.isNativePlatform() ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <button
                onClick={() => handleCapture(CameraSource.Camera)}
                disabled={!consentGiven || loading}
                style={{
                  ...styles.uploadArea,
                  padding: 'var(--space-6)',
                  opacity: consentGiven ? 1 : 0.6,
                  cursor: consentGiven ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-4)'
                }}
              >
                <span style={{ fontSize: '32px' }}>📸</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontWeight: '600', color: 'var(--color-text-primary)' }}>Take Live Photo</p>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-secondary)' }}>Use your camera to scan receipt</p>
                </div>
              </button>

              <button
                onClick={() => handleCapture(CameraSource.Photos)}
                disabled={!consentGiven || loading}
                style={{
                  ...styles.uploadArea,
                  padding: 'var(--space-6)',
                  opacity: consentGiven ? 1 : 0.6,
                  cursor: consentGiven ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-4)'
                }}
              >
                <span style={{ fontSize: '32px' }}>🖼️</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontWeight: '600', color: 'var(--color-text-primary)' }}>Choose from Gallery</p>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-secondary)' }}>Select from your photos</p>
                </div>
              </button>

              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={!consentGiven || loading}
                style={{
                  ...styles.uploadArea,
                  padding: 'var(--space-6)',
                  opacity: consentGiven ? 1 : 0.6,
                  cursor: consentGiven ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-4)'
                }}
              >
                <span style={{ fontSize: '32px' }}>📄</span>
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontWeight: '600', color: 'var(--color-text-primary)' }}>Browse Files / PDF</p>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-secondary)' }}>Select a file or PDF</p>
                </div>
              </button>
            </div>
          ) : (
            <div
              style={{
                ...styles.uploadArea,
                opacity: consentGiven ? 1 : 0.6,
                cursor: consentGiven ? 'pointer' : 'not-allowed'
              }}
              onClick={() => consentGiven ? fileInputRef.current?.click() : setError('Please consent to AI processing below before uploading a receipt.')}
            >
              <div style={styles.uploadIcon}>📸</div>
              <p style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--space-2)',
                margin: `0 0 var(--space-2) 0`
              }}>
                Take Photo or Choose File
              </p>
              <p style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                margin: 0
              }}>
                Upload a receipt (JPG, PNG, PDF - up to 10MB)
              </p>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,application/pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <label style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '12px', 
            fontSize: 'var(--font-size-sm)', 
            color: 'var(--color-text-secondary)', 
            cursor: 'pointer', 
            padding: 'var(--space-2)',
            backgroundColor: consentGiven ? 'transparent' : 'var(--color-bg-subtle)',
            borderRadius: 'var(--radius-default)',
            border: consentGiven ? '1px solid transparent' : '1px dashed var(--color-border-default)'
          }}>
            <input 
              type="checkbox" 
              checked={consentGiven} 
              onChange={handleConsentChange}
              style={{ 
                width: '18px', 
                height: '18px', 
                cursor: 'pointer',
                marginTop: '2px',
                accentColor: 'var(--color-primary)'
              }}
            />
            <span style={{ lineHeight: '1.4' }}>
              I consent to my receipt images being securely transmitted to OpenAI for automated data extraction. Read our <a href="/privacy" target="_blank" style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 'var(--font-weight-medium)' }}>Privacy Policy</a> for more details.
            </span>
          </label>
        </div>
      ) : (
        <>
          <div style={styles.preview}>
            {preview === 'pdf' ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--space-10)',
                backgroundColor: 'var(--color-bg-muted)',
                borderRadius: 'var(--radius-lg)',
              }}>
                <div style={{ fontSize: '64px', marginBottom: 'var(--space-4)' }}>📄</div>
                <p style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-primary)',
                  margin: `0 0 var(--space-2) 0`
                }}>
                  {selectedFile?.name}
                </p>
                <p style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-secondary)',
                  margin: 0
                }}>
                  PDF Ready to Upload
                </p>
              </div>
            ) : (
              <img src={preview} alt="Receipt preview" style={styles.previewImage} />
            )}
          </div>

          <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
            <button
              onClick={handleUpload}
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.primaryButton,
                ...(loading ? styles.disabledButton : {}),
              }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = 'var(--color-primary-hover)')}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = 'var(--color-primary)')}
            >
              {loading && <div style={styles.loader}></div>}
              {loading ? 'Processing...' : 'Upload & Extract Data'}
            </button>
            <button
              onClick={handleReset}
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                ...(loading ? styles.disabledButton : {}),
              }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = 'var(--color-bg-subtle)')}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = 'var(--color-bg-muted)')}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {error && (
        <div style={styles.error}>
          <strong>⚠️ Error: </strong>{error}
        </div>
      )}

      {result && (
        <div style={styles.result}>
          <h3 style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            marginBottom: 'var(--space-4)',
            color: 'var(--color-success-dark)',
            margin: `0 0 var(--space-4) 0`,
            letterSpacing: '-0.025em',
          }}>
            ✅ Receipt Processed Successfully!
          </h3>
          <div style={styles.resultItem}>
            <span style={styles.label}>Vendor:</span>
            <span style={styles.value}>{result.vendor_name || 'Unknown'}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.label}>Amount:</span>
            <span style={styles.value}>{result.currency_code || ''} {result.amount?.toFixed(2) || '0.00'}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.label}>Category:</span>
            <span style={styles.value}>{result.category || 'Uncategorized'}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.label}>Date:</span>
            <span style={styles.value}>{result.date || 'N/A'}</span>
          </div>
          {result.description && (
            <div style={styles.resultItem}>
              <span style={styles.label}>Description:</span>
              <span style={styles.value}>{result.description}</span>
            </div>
          )}
          {result.line_items && result.line_items.length > 0 && (
            <div style={{
              marginTop: 'var(--space-4)',
              padding: 'var(--space-3)',
              backgroundColor: 'rgba(16, 185, 129, 0.05)',
              borderRadius: 'var(--radius-default)',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <div style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-success-dark)',
                marginBottom: 'var(--space-3)'
              }}>
                📋 Itemized Details:
              </div>
              {result.line_items.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 'var(--space-2) 0',
                    borderBottom: idx < result.line_items.length - 1 ? '1px solid rgba(16, 185, 129, 0.2)' : 'none'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <span style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-success-dark)',
                      fontWeight: 'var(--font-weight-medium)'
                    }}>
                      {item.name}
                    </span>
                    {item.quantity && item.quantity > 1 && (
                      <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        marginLeft: 'var(--space-2)',
                        fontWeight: 'var(--font-weight-semibold)'
                      }}>
                        × {item.quantity}
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--color-success-dark)',
                    marginLeft: 'var(--space-3)'
                  }}>
                    ${item.price?.toFixed(2) || '0.00'}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: 'var(--space-3)',
            marginTop: 'var(--space-6)',
          }}>
            <button
              onClick={handleUploadNext}
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                flex: 1,
                marginTop: 0,
                marginLeft: 0,
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = 'var(--color-bg-subtle)')}
              onMouseLeave={(e) => (e.target.style.backgroundColor = 'var(--color-bg-muted)')}
            >
              Upload Next
            </button>
            <button
              onClick={handleFinishUploading}
              style={{
                ...styles.button,
                ...styles.primaryButton,
                flex: 1,
                marginTop: 0,
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = 'var(--color-primary-hover)')}
              onMouseLeave={(e) => (e.target.style.backgroundColor = 'var(--color-primary)')}
            >
              Finish Uploading
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default ReceiptUpload;
